import React from 'react';
import { motion } from 'motion/react';
import { Sparkles, ShoppingBag, Flame, Compass, Heart, Feather } from 'lucide-react';
import { INGREDIENTS, REVIEWS, INTERIORS } from '../data/candles';
import { CandleProduct } from '../types';
// Lazy load below-the-fold heavy components
const FinalCinematicEnding = React.lazy(() =>
  import('./FinalCinematicEnding').then((m) => ({ default: m.FinalCinematicEnding }))
);
const ProductShowcase = React.lazy(() =>
  import('./ProductShowcase').then((m) => ({ default: m.ProductShowcase }))
);
import { getMasterSceneAlpha, DESKTOP_SCENES } from '../utils/masterTimeline';
import { IntersectionLazySection } from './IntersectionLazySection';
import { HeaderAccountDropdown } from './HeaderAccountDropdown';

interface CinematicOverlayProps {
  scrollProgress: number;
  activeCandleIndex: number;
  setActiveCandleIndex: (idx: number) => void;
  onOpenCart: (candle?: CandleProduct) => void;
  onOpenFounderModal: () => void;
  onOpenCustomizerModal: () => void;
  onScrollToProgress: (target: number) => void;
  onTriggerShopTransition?: (origin?: { x: number; y: number } | React.MouseEvent) => void;
  onOpenAccount?: (tab?: string, mode?: string) => void;
  cartCount?: number;
}

export const CinematicOverlay: React.FC<CinematicOverlayProps> = React.memo(({
  scrollProgress,
  activeCandleIndex,
  setActiveCandleIndex,
  onOpenCart,
  onOpenFounderModal,
  onOpenCustomizerModal,
  onScrollToProgress,
  onTriggerShopTransition,
  onOpenAccount,
  cartCount = 0,
}) => {
  const p = scrollProgress;

  // Derive scene opacities from Master Timeline Engine (0.0 - 1.0)
  const a1 = getMasterSceneAlpha(p, DESKTOP_SCENES[0].start, DESKTOP_SCENES[0].end);
  const a2 = getMasterSceneAlpha(p, DESKTOP_SCENES[1].start, DESKTOP_SCENES[1].end);
  const a3 = getMasterSceneAlpha(p, DESKTOP_SCENES[2].start, DESKTOP_SCENES[2].end);
  const a4 = getMasterSceneAlpha(p, DESKTOP_SCENES[3].start, DESKTOP_SCENES[3].end);
  const a5 = getMasterSceneAlpha(p, DESKTOP_SCENES[4].start, DESKTOP_SCENES[4].end);
  const a6 = getMasterSceneAlpha(p, DESKTOP_SCENES[5].start, DESKTOP_SCENES[5].end);
  const a7 = getMasterSceneAlpha(p, DESKTOP_SCENES[6].start, DESKTOP_SCENES[6].end);
  const a8 = getMasterSceneAlpha(p, DESKTOP_SCENES[7].start, DESKTOP_SCENES[7].end);
  const a9 = getMasterSceneAlpha(p, DESKTOP_SCENES[8].start, DESKTOP_SCENES[8].end);
  const a10 = getMasterSceneAlpha(p, DESKTOP_SCENES[9].start, DESKTOP_SCENES[9].end);
  const a11 = getMasterSceneAlpha(p, DESKTOP_SCENES[10].start, DESKTOP_SCENES[10].end);
  const a12 = getMasterSceneAlpha(p, DESKTOP_SCENES[11].start, DESKTOP_SCENES[11].end);
  const a13 = getMasterSceneAlpha(p, DESKTOP_SCENES[12].start, DESKTOP_SCENES[12].end);
  const a14 = getMasterSceneAlpha(p, DESKTOP_SCENES[13].start, DESKTOP_SCENES[13].end, 0.20);

  return (
    <div className="fixed inset-0 z-10 pointer-events-none flex flex-col justify-between p-6 md:p-12 text-[#f5f5f0] selection:bg-amber-500/30">
      {/* Natural Centered Circular Candlelight Atmosphere */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[550px] h-[550px] rounded-full opacity-35 bg-[radial-gradient(circle_at_center,_rgba(255,185,75,0.32)_0%,_rgba(200,120,40,0.14)_35%,_rgba(80,45,15,0.04)_65%,_transparent_80%)] pointer-events-none filter blur-2xl" />
      {/* Soft Atmospheric Golden Foreground Surface at Bottom */}
      <div className="absolute inset-x-0 bottom-0 h-72 opacity-40 bg-gradient-to-t from-[#3d2b15]/50 via-[#26190b]/15 to-transparent pointer-events-none filter blur-lg" />

      {/* Top Header Bar */}
      <header className="flex items-center justify-between pointer-events-auto relative z-20">
        <div className="flex flex-col space-y-1">
          <div 
            onClick={() => onScrollToProgress(0.15)} 
            className="cursor-pointer group flex items-center space-x-3 mb-1"
          >
            <div className="w-7 h-7 rounded-full border border-white/20 flex items-center justify-center bg-black/40 backdrop-blur-md group-hover:border-white transition-colors">
              <Flame className="w-3.5 h-3.5 text-amber-300 group-hover:scale-110 transition-transform" />
            </div>
            <span className="font-serif text-2xl tracking-[0.2em] font-light text-[#f5f5f0]">
              NOOR PIECES
            </span>
          </div>
          <div className="hidden sm:flex flex-col text-[10px] font-sans uppercase tracking-[0.4em] opacity-40">
            <span>Handcrafted Soy Candles</span>
            <span>Poured in Small Batches</span>
          </div>
        </div>

        {/* Top Right Navigation: Collection Button ONLY */}
        <div className="flex items-center">
          <button
            onClick={(e) => {
              if (onTriggerShopTransition) {
                onTriggerShopTransition(e);
              } else {
                onScrollToProgress(0.81);
              }
            }}
            className="text-xs sm:text-sm font-serif uppercase tracking-[0.2em] font-semibold text-[#C8A45A] bg-black/50 backdrop-blur-md border border-[#C8A45A]/40 px-5 py-2 rounded-full cursor-pointer transition-all duration-300 hover:bg-[#C8A45A] hover:text-[#1A1817] hover:shadow-[0_0_20px_rgba(200,164,90,0.4)] flex items-center space-x-1.5 active:scale-95"
          >
            <span>✨ Collection</span>
          </button>
        </div>
      </header>

      {/* Left Artistic Vertical Text Sidebar */}
      <div className="hidden lg:flex absolute top-1/2 left-8 -translate-y-1/2 flex-col space-y-8 opacity-30 font-sans text-[9px] uppercase tracking-[0.4em] [writing-mode:vertical-rl] rotate-180 z-20 pointer-events-none">
        <span>Patience</span>
        <span>Warmth</span>
        <span>Intention</span>
      </div>

      {/* Main Dynamic Story Overlay Content */}
      <main className="flex-1 flex flex-col justify-center items-center text-center max-w-4xl mx-auto my-auto relative w-full pointer-events-auto z-20">
        {/* Darkness & Silence (0.00 - 0.09) */}
        <IntersectionLazySection scrollProgress={p} startProgress={DESKTOP_SCENES[0].start} alpha={a1} isHero={true}>
          <div
            style={{
              opacity: a1,
              transform: `translate3d(0, ${(1 - a1) * 20}px, 0) scale(${0.98 + 0.02 * a1})`,
              willChange: 'transform, opacity',
              pointerEvents: a1 > 0.5 ? 'auto' : 'none',
              position: 'absolute',
            }}
            className="space-y-4"
          >
            <p className="text-[10px] uppercase tracking-[0.4em] font-sans text-amber-300/80">
              Silence & Intention
            </p>
            <h1 className="text-3xl md:text-5xl font-serif font-light text-[#f5f5f0] tracking-wide">
              In complete darkness, light begins with intention.
            </h1>
            <p className="text-xs font-sans text-amber-200/60 uppercase tracking-[0.3em]">
              Slowly scroll to strike the spark
            </p>
          </div>
        </IntersectionLazySection>

        {/* Ignition (0.05 - 0.16) */}
        <IntersectionLazySection scrollProgress={p} startProgress={DESKTOP_SCENES[1].start} alpha={a2} isHero={true}>
          <div
            style={{
              opacity: a2,
              transform: `translate3d(0, ${(1 - a2) * 20}px, 0) scale(${0.98 + 0.02 * a2})`,
              willChange: 'transform, opacity',
              pointerEvents: a2 > 0.5 ? 'auto' : 'none',
              position: 'absolute',
            }}
            className="space-y-3"
          >
            <p className="text-[10px] uppercase tracking-[0.4em] font-sans opacity-60 text-amber-300">
              The Ignition
            </p>
            <h2 className="text-4xl md:text-6xl font-serif font-light text-[#f5f5f0]">
              A single flame transforms room and soul.
            </h2>
          </div>
        </IntersectionLazySection>

        {/* Logo & Title (0.12 - 0.23) */}
        <IntersectionLazySection scrollProgress={p} startProgress={DESKTOP_SCENES[2].start} alpha={a3} isHero={true}>
          <div
            style={{
              opacity: a3,
              transform: `translate3d(0, ${(1 - a3) * 20}px, 0) scale(${0.98 + 0.02 * a3})`,
              willChange: 'transform, opacity',
              pointerEvents: a3 > 0.5 ? 'auto' : 'none',
              position: 'absolute',
            }}
            className="space-y-6"
          >
            <span className="text-[10px] font-sans uppercase tracking-[0.5em] opacity-60 text-amber-200">
              Handmade / Intention
            </span>
            <h1 className="text-6xl md:text-9xl font-serif font-extralight text-[#f5f5f0] tracking-[0.15em] uppercase mix-blend-difference leading-[0.85]">
              NOOR PIECES
            </h1>
            <p className="text-lg md:text-2xl font-light tracking-[0.25em] uppercase opacity-70 max-w-xl mx-auto">
              Light a Moment.
            </p>

            <div className="flex flex-wrap justify-center gap-4 pt-6">
              <button
                onClick={() => onScrollToProgress(0.66)}
                className="px-8 py-3 bg-[#f5f5f0] text-black rounded-full text-[11px] font-sans uppercase tracking-[0.2em] font-semibold transition-all hover:bg-amber-200 cursor-pointer shadow-2xl hover:scale-105"
              >
                Shop the Collection
              </button>
              <button
                onClick={onOpenFounderModal}
                className="px-8 py-3 border border-white/20 rounded-full text-[11px] font-sans uppercase tracking-[0.2em] hover:bg-white hover:text-black transition-all cursor-pointer backdrop-blur-md"
              >
                Our Story
              </button>
            </div>
          </div>
        </IntersectionLazySection>

        {/* Into the Flame (0.19 - 0.30) */}
        <IntersectionLazySection scrollProgress={p} startProgress={DESKTOP_SCENES[3].start} alpha={a4}>
          <div
            style={{
              opacity: a4,
              transform: `translate3d(0, ${(1 - a4) * 20}px, 0) scale(${0.98 + 0.02 * a4})`,
              willChange: 'transform, opacity',
              pointerEvents: a4 > 0.5 ? 'auto' : 'none',
              position: 'absolute',
            }}
            className="space-y-2"
          >
            <p className="text-2xl md:text-4xl font-serif italic text-[#f5f5f0] font-light">
              Step inside the warmth...
            </p>
          </div>
        </IntersectionLazySection>

        {/* SCENE 5: Molten Core (0.28 - 0.35) */}
        <IntersectionLazySection scrollProgress={p} startProgress={DESKTOP_SCENES[4].start} alpha={a5}>
          <div
            style={{
              opacity: a5,
              transform: `translate3d(0, ${(1 - a5) * 16 - 20}px, 0) scale(${0.98 + 0.02 * a5})`,
              willChange: 'transform, opacity',
              pointerEvents: a5 > 0.5 ? 'auto' : 'none',
              position: 'absolute',
            }}
            className="space-y-4 max-w-xl border border-[#C8A45A]/25 bg-[#0a0705]/20 backdrop-blur-md p-6 md:p-8 rounded-2xl shadow-[0_0_50px_rgba(0,0,0,0.5)]"
          >
            <p className="text-[10px] font-sans uppercase tracking-[0.4em] text-amber-300/90 font-semibold">
              The Alchemy of Pure Soy Wax
            </p>
            <h3 className="text-2xl md:text-3xl font-serif text-[#f5f5f0] font-light leading-snug">
              Golden botanical oils dancing in molten liquid warmth.
            </h3>
            <p className="text-xs font-sans text-[#f5f5f0]/80 leading-relaxed font-light tracking-wider">
              We blend 100% renewable soy and coconut wax with therapeutic-grade botanical essences. Zero parabens, zero synthetic phthalates.
            </p>
          </div>
        </IntersectionLazySection>

        {/* SCENE 6: Rashmi's Workshop (0.35 - 0.42) */}
        <IntersectionLazySection scrollProgress={p} startProgress={DESKTOP_SCENES[5].start} alpha={a6}>
          <div
            style={{
              opacity: a6,
              transform: `translate3d(0, ${(1 - a6) * 16 - 20}px, 0) scale(${0.98 + 0.02 * a6})`,
              willChange: 'transform, opacity',
              pointerEvents: a6 > 0.5 ? 'auto' : 'none',
              position: 'absolute',
            }}
            className="space-y-5 max-w-xl border border-[#C8A45A]/25 bg-[#0a0705]/25 backdrop-blur-md p-6 md:p-8 rounded-3xl shadow-[0_0_50px_rgba(0,0,0,0.5)]"
          >
            <div className="w-10 h-10 rounded-full border border-[#C8A45A]/30 mx-auto flex items-center justify-center bg-amber-500/10">
              <Feather className="w-4 h-4 text-amber-300" />
            </div>
            <p className="text-2xl md:text-4xl font-serif text-[#f5f5f0] font-extralight italic">
              "Every candle begins with patient hands."
            </p>
            <p className="text-[10px] font-sans text-amber-300/90 tracking-[0.3em] uppercase font-semibold">
              Rashmi • Founder & Master Chandler
            </p>
            <button
              onClick={onOpenFounderModal}
              className="text-[11px] font-sans text-amber-200 border-b border-amber-300/40 hover:border-amber-200 transition-colors uppercase tracking-[0.2em] cursor-pointer"
            >
              Read Rashmi’s Artisanal Philosophy
            </button>
          </div>
        </IntersectionLazySection>

        {/* SCENE 7: Floating Ingredients (0.40 - 0.52) */}
        <IntersectionLazySection scrollProgress={p} startProgress={DESKTOP_SCENES[6].start} alpha={a7}>
          <div
            style={{
              opacity: a7,
              transform: `translate3d(0, ${(1 - a7) * 20}px, 0) scale(${0.98 + 0.02 * a7})`,
              willChange: 'transform, opacity',
              pointerEvents: a7 > 0.5 ? 'auto' : 'none',
              position: 'absolute',
            }}
            className="space-y-6 w-full"
          >
            <p className="text-[10px] font-sans uppercase tracking-[0.4em] opacity-60 text-amber-300">
              Pure Ingredients
            </p>
            <h2 className="text-3xl md:text-5xl font-serif font-light text-[#f5f5f0]">
              Grown by Nature. Chosen with Respect.
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl mx-auto pt-4">
              {INGREDIENTS.slice(0, 4).map((ing, i) => (
                <div
                  key={i}
                  className="p-4 border border-white/10 bg-black/50 backdrop-blur-md rounded-xl text-left hover:border-white/30 transition-all"
                >
                  <p className="text-xs font-serif text-amber-200">{ing.name}</p>
                  <p className="text-[9px] font-sans text-white/50 uppercase tracking-widest mt-1">
                    {ing.origin}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </IntersectionLazySection>

        {/* Assembly & Creation (0.48 - 0.59) */}
        <IntersectionLazySection scrollProgress={p} startProgress={DESKTOP_SCENES[7].start} alpha={a8}>
          <div
            style={{
              opacity: a8,
              transform: `translate3d(0, ${(1 - a8) * 20}px, 0) scale(${0.98 + 0.02 * a8})`,
              willChange: 'transform, opacity',
              pointerEvents: a8 > 0.5 ? 'auto' : 'none',
              position: 'absolute',
            }}
            className="space-y-4 max-w-lg border border-white/10 bg-black/50 backdrop-blur-md p-6 md:p-8 rounded-2xl"
          >
            <p className="text-[10px] font-sans uppercase tracking-[0.4em] opacity-60 text-amber-300">
              Small Batch Craftsmanship
            </p>
            <h3 className="text-2xl md:text-4xl font-serif text-[#f5f5f0] font-light">
              Crafted in Small Batches
            </h3>
            <p className="text-xs font-sans text-white/70 font-light leading-relaxed">
              Poured in temperature-controlled glass vessels and cured for 14 days to lock in full fragrance throw.
            </p>
          </div>
        </IntersectionLazySection>

        {/* SCENE 9: Scent Realms (0.55 - 0.67) */}
        <IntersectionLazySection scrollProgress={p} startProgress={DESKTOP_SCENES[8].start} alpha={a9}>
          <div
            style={{
              opacity: a9,
              transform: `translate3d(0, ${(1 - a9) * 20}px, 0) scale(${0.98 + 0.02 * a9})`,
              willChange: 'transform, opacity',
              pointerEvents: a9 > 0.5 ? 'auto' : 'none',
              position: 'absolute',
            }}
            className="space-y-6 max-w-2xl"
          >
            <p className="text-[10px] font-sans uppercase tracking-[0.4em] opacity-60 text-amber-300">
              Immersive Scent Realms
            </p>
            <h2 className="text-3xl md:text-5xl font-serif font-light text-[#f5f5f0]">
              Fragrances that transport you to another world.
            </h2>
            <div className="flex justify-center space-x-3 pt-2">
              <button
                onClick={onOpenCustomizerModal}
                className="px-8 py-3 rounded-full border border-white/20 bg-black/60 hover:bg-white hover:text-black font-sans text-[11px] tracking-[0.2em] uppercase transition-all"
              >
                Create Custom Fragrance Blend
              </button>
            </div>
          </div>
        </IntersectionLazySection>

        {/* SCENE 10: Masterpiece Gallery (0.63 - 0.76) */}
        <IntersectionLazySection scrollProgress={p} startProgress={DESKTOP_SCENES[9].start} alpha={a10}>
          <React.Suspense fallback={null}>
            <ProductShowcase
              activeProductIndex={activeCandleIndex}
              onSelectProductIndex={setActiveCandleIndex}
              onOpenCart={onOpenCart}
              alpha={a10}
            />
          </React.Suspense>
        </IntersectionLazySection>

        {/* SCENE 11: Living Interiors (0.72 - 0.84) */}
        <IntersectionLazySection scrollProgress={p} startProgress={DESKTOP_SCENES[10].start} alpha={a11}>
          <div
            style={{
              opacity: a11,
              transform: `translate3d(0, ${(1 - a11) * 20}px, 0) scale(${0.98 + 0.02 * a11})`,
              willChange: 'transform, opacity',
              pointerEvents: a11 > 0.5 ? 'auto' : 'none',
              position: 'absolute',
            }}
            className="space-y-6 max-w-2xl border border-white/10 bg-black/60 backdrop-blur-xl p-8 rounded-3xl"
          >
            <p className="text-[10px] font-sans uppercase tracking-[0.4em] opacity-60 text-amber-300">
              Living in Warmth
            </p>
            <h2 className="text-3xl md:text-4xl font-serif text-[#f5f5f0] font-light">
              Designed to harmonize with beautiful architectural interiors.
            </h2>
            <div className="grid grid-cols-2 gap-3 pt-2">
              {INTERIORS.map((item) => (
                <div key={item.id} className="p-3 border border-white/10 bg-white/5 rounded-xl text-left">
                  <p className="text-xs font-serif text-amber-200">{item.title}</p>
                  <p className="text-[11px] font-serif text-white/60 italic mt-1">{item.quote}</p>
                </div>
              ))}
            </div>
          </div>
        </IntersectionLazySection>

        {/* SCENE 12: The Unboxing (0.80 - 0.91) */}
        <IntersectionLazySection scrollProgress={p} startProgress={DESKTOP_SCENES[11].start} alpha={a12}>
          <div
            style={{
              opacity: a12,
              transform: `translate3d(0, ${(1 - a12) * 20}px, 0) scale(${0.98 + 0.02 * a12})`,
              willChange: 'transform, opacity',
              pointerEvents: a12 > 0.5 ? 'auto' : 'none',
              position: 'absolute',
            }}
            className="space-y-4 max-w-xl border border-white/10 bg-black/70 backdrop-blur-2xl p-8 rounded-3xl"
          >
            <p className="text-[10px] font-sans uppercase tracking-[0.4em] opacity-60 text-amber-300">
              Unwrap a Moment of Slow Living
            </p>
            <h2 className="text-3xl md:text-4xl font-serif text-[#f5f5f0] font-light">
              Luxury Linen Packaging & Embossed Gold Foil
            </h2>
            <p className="text-xs font-sans text-white/70 font-light leading-relaxed">
              Every order includes a custom wooden matchbox, a velvet protective dust bag, and a handwritten thank you card from Rashmi.
            </p>
          </div>
        </IntersectionLazySection>

        {/* SCENE 13: Customer Reviews (0.87 - 0.96) */}
        <IntersectionLazySection scrollProgress={p} startProgress={DESKTOP_SCENES[12].start} alpha={a13}>
          <div
            style={{
              opacity: a13,
              transform: `translate3d(0, ${(1 - a13) * 20}px, 0) scale(${0.98 + 0.02 * a13})`,
              willChange: 'transform, opacity',
              pointerEvents: a13 > 0.5 ? 'auto' : 'none',
              position: 'absolute',
            }}
            className="space-y-6 w-full max-w-3xl"
          >
            <p className="text-[10px] font-sans uppercase tracking-[0.4em] opacity-60 text-amber-300">
              Letters from Quiet Homes
            </p>
            <h2 className="text-3xl md:text-4xl font-serif text-[#f5f5f0] font-light">
              Illuminated Stories
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {REVIEWS.map((rev) => (
                <div
                  key={rev.id}
                  className="p-6 border border-white/10 bg-black/60 backdrop-blur-xl rounded-2xl text-left space-y-3"
                >
                  <p className="font-serif italic text-white/90 text-sm leading-relaxed">
                    "{rev.quote}"
                  </p>
                  <div className="flex justify-between items-center text-[10px] font-sans text-amber-300/80 uppercase tracking-widest pt-2 border-t border-white/10">
                    <span>— {rev.author} ({rev.location})</span>
                    <span>{rev.candleName}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </IntersectionLazySection>

        {/* SCENE 14: Emotional Final Ending (0.93 - 1.00) */}
        <IntersectionLazySection scrollProgress={p} startProgress={DESKTOP_SCENES[13].start} alpha={a14}>
          <div
            style={{
              opacity: a14,
              willChange: 'opacity',
              pointerEvents: a14 > 0.5 ? 'auto' : 'none',
              position: 'absolute',
            }}
            className="w-full"
          >
            <React.Suspense fallback={null}>
              <FinalCinematicEnding
                onTriggerShopTransition={onTriggerShopTransition}
                onOpenCart={onOpenCart}
              />
            </React.Suspense>
          </div>
        </IntersectionLazySection>
      </main>

      {/* Clean Minimalist Website Footer */}
      <footer className="flex items-center justify-between text-[10px] font-sans opacity-40 uppercase tracking-[0.4em] pointer-events-auto z-20">
        <div className="flex items-center space-x-3">
          <span>NOOR PIECES ATELIER</span>
        </div>

        <div className="flex items-center space-x-2">
          <span>Scroll to Discover</span>
          <div className="w-3.5 h-6 rounded-full border border-white/30 flex justify-center p-1">
            <motion.div
              animate={{ y: [0, 6, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className="w-1 h-1 bg-white rounded-full"
            />
          </div>
        </div>

        <span className="hidden sm:inline">Handcrafted with Intention</span>
      </footer>
    </div>
  );
});
